import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { Helmet } from "react-helmet";
import { useSelector } from "react-redux";

import TopBar from "../../../components/TopBar";
import BottomBar from "../../../components/BottomBar";
import EmailHarvesting from "../../../components/EmailHarvesting";
import ProductDownload from "../../../components/ProductDownload";
import { getProduct } from "../../../util/api/productApi";
import MainBanner from "../../../components/Products/ProtoneBundle/MainBanner";
import IncludedProducts from "../../../components/Products/ProtoneBundle/IncludedProducts";
import BundlePurchase from "../../../components/Products/ProtoneBundle/BundlePurchase";
import { StoreProduct } from "../../../components/Products/ProtoneBundle/data";
import deadHorseImage from "../../../static/images/protone_pedals_bundle/dead-horse.webp";

export const PRODUCT_ID = "protone-pedals-bundle";
const CANONICAL_URL = `https://wavemind.net/product/${PRODUCT_ID}`;
const DESCRIPTION = "ProTone Pedals Bundle brings together Dead Horse Overdrive, Cor Mid Boost and Monster Fuzz: three pedal plugins for drive, midrange shaping and fuzz.";

// Bundle-specific page shell with shared checkout and download controls.
const ProtoneBundlePage = ({ useHelmet = true }) => {
  const [product, setProduct] = useState<StoreProduct>();
  const [loading, setLoading] = useState(true);
  const [retry, setRetry] = useState(0);
  const [downloadID, setDownloadID] = useState<string>();
  const token = useSelector((state: { token?: unknown }) => state?.token);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setProduct(undefined);

    getProduct(PRODUCT_ID).then((response) => {
      if (active && response.data?.metadata?.product_id === PRODUCT_ID) {
        setProduct(response.data);
      }
    }).catch(() => {
      // Keep the overview available when pricing is unavailable; offer retry.
    }).finally(() => {
      if (active) setLoading(false);
    });

    return () => { active = false; };
  }, [token, retry]);

  const purchase = <BundlePurchase product={product} loading={loading} onRetry={() => setRetry((value) => value + 1)} />;
  const socialImage = new URL(deadHorseImage, "https://wavemind.net").href;

  return <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", background: "#100f0e", color: "#f9f5ee" }}>
    {useHelmet && <Helmet>
      <title>WaveMind Audio | ProTone Pedals Bundle</title>
      <meta name="description" content={DESCRIPTION} />
      <link rel="canonical" href={CANONICAL_URL} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="WaveMind Audio | ProTone Pedals Bundle" />
      <meta property="og:description" content={DESCRIPTION} />
      <meta property="og:url" content={CANONICAL_URL} />
      <meta property="og:image" content={socialImage} />
      <meta property="og:image:alt" content="Dead Horse Overdrive, included in ProTone Pedals Bundle" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="WaveMind Audio | ProTone Pedals Bundle" />
      <meta name="twitter:description" content={DESCRIPTION} />
      <meta name="twitter:image" content={socialImage} />
      <meta name="twitter:image:alt" content="Dead Horse Overdrive, included in ProTone Pedals Bundle" />
    </Helmet>}
    <TopBar menuValue="1" />
    <ProductDownload open={downloadID !== undefined} productID={downloadID} onClose={() => setDownloadID(undefined)} requireEmail />
    <main>
      <MainBanner>{purchase}</MainBanner>
      <IncludedProducts onTrial={setDownloadID} />
      <Box component="section" aria-labelledby="bundle-purchase-title" sx={{ px: { xs: 3, sm: 5 }, py: { xs: 7, sm: 9 } }}>
        <Box sx={{ maxWidth: "1200px", mx: "auto", display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 4, alignItems: "center" }}>
          <Box>
            <Typography id="bundle-purchase-title" variant="h2" sx={{ fontSize: { xs: "2rem", sm: "2.75rem" }, fontWeight: 600 }}>Build your ProTone pedalboard.</Typography>
            <Typography sx={{ mt: 2, maxWidth: "500px", lineHeight: 1.8, color: "#d4cec7" }}>Get Dead Horse Overdrive, Cor Mid Boost and Monster Fuzz together in one bundle.</Typography>
          </Box>
          <Box>{purchase}</Box>
        </Box>
      </Box>
    </main>
    <EmailHarvesting />
    <BottomBar />
  </Box>;
};

export default ProtoneBundlePage;
